<template>
  <div v-if="token">
    <loader :loading="loading"/>
    <page-title title="Editar Token">
      <div class="btnGroup" slot="header-right">
        <button @click="changeStatus(false)" v-bind:class="{ 'is-active': !statusToken }" class="btn btn--info btn--outline">
          Inativo
        </button>
        <button @click="changeStatus(true) "v-bind:class="{ 'is-active': statusToken }" class="btn btn--info btn--outline">
          Ativo
        </button>
      </div>
    </page-title>

    <panel>
      <div class="l-row">
        <form-group label="Token" :columns="4">
          <div class="margin">
            {{ token.authorization }}
            <button type="button" @click="copied()" class="btn btn--sm btn--info copy hint hint--top" data-label="Clique para copiar token" v-clipboard:copy="token.authorization">
              <i class="icon icon-copy2"></i>
            </button>
          </div>
        </form-group>

        <form-group label="Nome" :columns="4">
          <input class="form-input" type="text" v-model="token.description">
        </form-group>
      </div>
    </panel>

    <panel title="Permissões de produto">
      <div class="l-row">
        <form-group label="" :columns="12">
          <select v-model="permissionProductSelected" class="form-select" @change="getPermissionProduct()">
            <option value="notPermission">Sem permissão</option>
            <option value="readOnly">Somente leitura</option>
            <option value="readAndWriting">Leitura e escrita</option>
            <option value="advancedOptionsProduct">Opções avançadas</option>
          </select>
        </form-group>
      </div>

      <div class="l-row" v-if="permissionProductSelected === 'advancedOptionsProduct'">
        <div class="l-col-12@md">
          <form-group :label="option.resource" :columns="4" v-for="option in productAdvancedOptions" :key="option">
            <label class="form-checkbox" v-for="action in Object.keys(option.actions)">
              <input v-model="option.actions[action]" type="checkbox">
              <span class="form-checkbox__indicator"></span>
              <span class="form-checkbox__text">{{ action }}</span>
            </label>
          </form-group>
        </div>
      </div>
    </panel>

    <panel title="Permissões de pedido">
      <div class="l-row">
        <form-group label="" :columns="12">
          <select v-model="permissionOrderSelected" class="form-select" @change="getPermissionOrder()">
            <option value="notPermission">Sem permissão</option>
            <option value="readOnly">Somente leitura</option>
            <option value="readAndWriting">Leitura e escrita</option>
            <option value="advancedOptionsOrder">Opções avançadas</option>
          </select>
        </form-group>
      </div>

      <div class="l-row" v-if="permissionOrderSelected === 'advancedOptionsOrder'">
        <div class="l-col-12@md">
          <form-group v-if="option.resource !== '/service/status'" :label="option.resource" :columns="4" v-for="option in orderAdvancedOptions" :key="option">
            <label class="form-checkbox" v-for="action in Object.keys(option.actions)">
              <input v-model="option.actions[action]" type="checkbox">
              <span class="form-checkbox__indicator"></span>
              <span class="form-checkbox__text">{{ action }}</span>
            </label>
          </form-group>
        </div>
      </div>
    </panel>

    <panel>
      <button :disabled="saving" @click="save()" v-bind:class="{ 'is-loading': saving }" class="btn btn--success" type="submit">Salvar</button>
    </panel>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import Panel from '@/components/template/Panel'
  import Loader from '@/components/template/Loader'
  import PageTitle from '@/components/template/PageTitle'
  import FormGroup from '@/components/template/FormGroup'
  import api from '@/api'
  import notifier from '@/notifier'
  import { cloneDeep, isEqual, concat } from 'lodash'
  import { productSimpleOptions, orderSimpleOptions, productAdvancedOptions, orderAdvancedOptions } from '@/storage/tokenPermissions'

  export default {
    data: () => ({
      token: null,
      statusToken: null,
      saving: false,
      loading: true,
      permissionProductSelected: 'advancedOptionsProduct',
      permissionOrderSelected: 'advancedOptionsOrder',
      productSimpleOptions: null,
      orderSimpleOptions: null,
      productAdvancedOptions: [],
      orderAdvancedOptions: []
    }),
    props: {
      identifier: {
        type: String,
        required: true
      }
    },
    async created () {
      this.productSimpleOptions = cloneDeep(productSimpleOptions)
      this.orderSimpleOptions = cloneDeep(orderSimpleOptions)
      this.productAdvancedOptions = cloneDeep(productAdvancedOptions)
      this.orderAdvancedOptions = cloneDeep(orderAdvancedOptions)

      this.token = this.getToken(this.identifier)
      if (!this.token) {
        await this.addToken(this.identifier)
        this.token = this.getToken(this.identifier)
      }

      const tokenPermissions = this.token.permissions
      const productPermissions = tokenPermissions.filter(permission => permission.resource.indexOf('product') !== -1)
      const orderPermissions = tokenPermissions.filter(permission => permission.resource.indexOf('order') !== -1)

      if (!productPermissions.length) {
        this.permissionProductSelected = 'notPermission'
      } else if (isEqual(productPermissions, this.productSimpleOptions.readOnly)) {
        this.permissionProductSelected = 'readOnly'
      } else if (isEqual(productPermissions, this.productSimpleOptions.readAndWriting)) {
        this.permissionProductSelected = 'readAndWriting'
      } else {
        for (let i in productPermissions) {
          const actions = productPermissions[i].actions
          for (let j in actions) {
            const action = actions[j]
            const productAdvancedOption = this.productAdvancedOptions.find(option => option.resource === productPermissions[i].resource)
            productAdvancedOption.actions[action] = true
          }
        }
      }

      if (!orderPermissions.length) {
        this.permissionOrderSelected = 'notPermission'
      } else if (isEqual(orderPermissions, this.orderSimpleOptions.readOnly)) {
        this.permissionOrderSelected = 'readOnly'
      } else if (isEqual(orderPermissions, this.orderSimpleOptions.readAndWriting)) {
        this.permissionOrderSelected = 'readAndWriting'
      } else {
        for (let i in orderPermissions) {
          const actions = orderPermissions[i].actions
          for (let j in actions) {
            const action = actions[j]
            const orderAdvancedOption = this.orderAdvancedOptions.find(option => option.resource === orderPermissions[i].resource)

            if (orderAdvancedOption === undefined) {
              continue
            }

            orderAdvancedOption.actions[action] = true
          }
        }
      }

      this.statusToken = this.token.status

      this.loading = false
    },
    methods: {
      ...mapActions('toolApi', [
        'addToken'
      ]),
      copied () {
        notifier.success('Token copiado com sucesso!', 'Sucesso')
      },
      changeStatus (status) {
        api.changeStatusToken({
          status: status,
          _id: this.identifier
        })
          .then(() => {
            this.statusToken = status
            notifier.success('Status alterado com sucesso!', 'Alterado')
          })
          .catch(() => notifier.error('Falha ao alterar o status.', 'Erro'))
      },
      save () {
        this.saving = true

        let token = {
          description: this.token.description,
          _id: this.identifier
        }

        token.permissions = concat(this.getPermissionProduct(), this.getPermissionOrder())

        api.editToken(token)
          .then(() => {
            notifier.success('Token alterado com sucesso!', 'Alterado')
          })
          .catch(() => notifier.error('Falha ao alterar o token.', 'Erro'))

        this.saving = false
      },
      getPermissionProduct () {
        if (this.permissionProductSelected === 'notPermission') {
          return []
        }

        if (this.permissionProductSelected === 'advancedOptionsProduct') {
          return this.productAdvancedOptions.map(advancedOption => {
            const actions = []

            for (let i in Object.keys(advancedOption.actions)) {
              const method = Object.keys(advancedOption.actions)[i]

              if (advancedOption.actions[method]) {
                actions.push(method)
              }
            }

            return {
              resource: advancedOption.resource,
              actions: actions
            }
          })
            .filter(permission => permission.actions.length)
        }

        return this.productSimpleOptions[this.permissionProductSelected]
      },
      getPermissionOrder () {
        if (this.permissionOrderSelected === 'notPermission') {
          return []
        }

        if (this.permissionOrderSelected === 'advancedOptionsOrder') {
          return this.orderAdvancedOptions.map(advancedOption => {
            const actions = []

            for (let i in Object.keys(advancedOption.actions)) {
              const method = Object.keys(advancedOption.actions)[i]

              if (advancedOption.actions[method]) {
                actions.push(method)
              }
            }

            return {
              resource: advancedOption.resource,
              actions: actions
            }
          })
            .filter(permission => permission.actions.length)
        }

        return this.orderSimpleOptions[this.permissionOrderSelected]
      }
    },
    computed: {
      ...mapGetters('toolApi', [
        'getToken'
      ])
    },
    components: {
      Panel,
      Loader,
      PageTitle,
      FormGroup
    }
  }
</script>

<style lang="sass" scoped>
  .margin
    margin-top: 8px

  .copy
    margin-left: 5px
    margin-bottom: 3px
</style>



// WEBPACK FOOTER //
// Edit.vue?b9886244